import { logger } from 'logger'
import Perfume from 'perfume.js'
import Config from 'config'
import { version as EditorVersion } from '@slite/editor'

/**
 * @typedef {object} Point
 * @property {number} value the point's value
 * @property {string=} timestamp A given timestamp, will be Date.now() if not given
 *
 * @typedef {object} Tag
 * @property {string} name The tag's name
 * @property {string} value The tag's value
 *
 * @typedef {object} Metric
 * @property {string} name the metric name
 * @property {string} type Either "count" or "gauge"
 * @property {Point[]} points
 * @property {string} service Either "app" or "editor"
 * @property {Tag[]=} tags An array of tags to add to this metric
 *
 * @typedef {object} IncrementCounterInput
 * @property {string} name the metric name
 * @property {string} [service="app"] Either "app" or "editor"
 * @property {Tag[]=} tags An array of tags to add to this metric
 *
 * @typedef {object} GaugeInput
 * @property {string} name the metric name
 * @property {string} [service="app"] Either "app" or "editor"
 * @property {number} value The gauge value
 * @property {Tag[]=} tags An array of tags to add to this metric
 */

const METRIC_FLUSH_INTERVAL = 2000

let nextCallback = null
const pendingMetrics = []

async function processPendingMetrics() {
  const metrics = pendingMetrics.splice(0, 50)

  if (!metrics.length) {
    nextCallback = null
    return
  }

  try {
    await fetch(Config.metricsEndpoint, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      method: 'post',
      body: JSON.stringify({
        query: `mutation($input: PushMetricsInput!) {
    sendMetrics(input: $input) {
      success
    }
  }`,
        variables: { input: { metrics } },
      }),
    })

    nextCallback = null
  } catch (error) {
    if (window.bugsnag) {
      window.bugsnag.notify(error)
    }

    nextCallback = null
  }

  if (pendingMetrics.length) {
    scheduleNextProcess()
  }
}

function scheduleNextProcess() {
  // There's a callback pending. Waiting for it to complete before sending stuff there
  if (nextCallback) {
    return
  }

  nextCallback = window.requestIdleCallback(() => processPendingMetrics(), {
    timeout: METRIC_FLUSH_INTERVAL,
  })
}

/**
 * @description This is the low level API used to send metrics to datadog
 * @param {string} Config.metricsEndpoint the metrics endpoint
 * @param {Metric[]} metrics the metric to send
 */
export function sendMetrics(metrics) {
  metrics = metrics.map(metric => {
    return {
      ...metric,
      tags: (metric.tags || []).concat([
        { name: 'env', value: Config.environment },
        { name: 'app-version', value: Config.appVersion },
        { name: 'editor-version', value: EditorVersion },
      ]),
    }
  })

  pendingMetrics.push(...metrics)
  scheduleNextProcess()
}

/**
 * @param {IncrementCounterInput} metrics the metric to send
 */
export function incrementMetric({ name, service = 'app', tags = undefined }) {
  sendMetrics([
    {
      name,
      service,
      type: 'count',
      points: [{ value: 1 }],
      tags,
    },
  ])
}

/**
 * @param {GaugeInput} metrics the metric to send
 */
export function gaugeMetric({
  name,
  value,
  service = 'app',
  tags = undefined,
}) {
  sendMetrics([
    {
      name,
      service,
      type: 'gauge',
      points: [{ value }],
      tags,
    },
  ])
}

const perfume = new Perfume({
  firstPaint: true,
  logPrefix: 'Performance monitoring:',
  logging: false,
  analyticsTracker: ({ metricName, data, duration }) => {
    logger.debug('Performance monitoring:', metricName, duration)
    gaugeMetric({ name: metricName, value: Math.floor(duration) })
  },
})

export function measureTimingStart(id) {
  perfume.start(id)
}

export function measureTimingEnd(id) {
  perfume.endPaint(id)
}
